import React, { useMemo } from 'react';

import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner } from '~components';
import { TableView } from '~components/table-view';

import PaymentStatusBadge from '~direct-debits/components/payment-batches/PaymentStatusBadge';
import { BatchClientRow, columnsFactory } from '~direct-debits/forms/payment-batches/batchDetail';

import { useGetPaymentBatchDetailQuery } from '~direct-debits/actions/paymentBatches';
import { PaymentBatchClient } from '~direct-debits/models';

interface ComponentProps {
  batchId: string;
  onBack: () => void;
  onViewInvoices: (clientId: number) => void;
  onRefund?: (row: PaymentBatchClient) => void;
  onReverse?: (row: PaymentBatchClient) => void;
}

const HeaderRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="d-flex flex-column me-4">
    <span className="text-muted small">{label}</span>
    <span className="fw-semibold">{value}</span>
  </div>
);

const PaymentBatchDetail: React.FC<ComponentProps> = ({ batchId, onBack, onViewInvoices, onRefund, onReverse }) => {
  const { data, isFetching, isError, error } = useGetPaymentBatchDetailQuery({ batch_id: batchId });

  const rows = useMemo<BatchClientRow[]>(
    () =>
      (data?.clients ?? []).map((client) => ({
        ...client,
        // TableView requires `id` — fall back to client_id which is unique
        // within the batch.
        id: client.client_id,
      })),
    [data?.clients],
  );

  const columns = useMemo(
    () => columnsFactory({ onViewInvoices, onRefund, onReverse }),
    [onViewInvoices, onRefund, onReverse],
  );

  return (
    <div className="p-2 dd-payment-batch-detail" data-cy="dd-payment-batch-detail">
      <div className="mb-3">
        {/*
          Spec calls for an <a> styled as a link rather than a Button. We keep
          it keyboard-accessible by using a real anchor with a real href fallback
          that is suppressed on click. The implicit ARIA role of an <a> with an
          href is already "link", so no explicit role is needed.
        */}
        <a
          href="#back-to-batches"
          className="link-primary"
          data-cy="dd-payment-batch-detail-back"
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onBack();
            }
          }}
        >
          ← Back to Batches
        </a>
      </div>
      {isError && <ErrorResponse message={getErrorMessage(error)} />}
      {isFetching && !data && (
        <div className="my-3">
          <Spinner loading />
        </div>
      )}
      {data && (
        <>
          <div
            className="d-flex flex-wrap align-items-center mb-3 p-2 border rounded"
            data-cy="dd-payment-batch-detail-header"
          >
            <HeaderRow label="Batch ID" value={data.reference} />
            <HeaderRow label="Status" value={<PaymentStatusBadge status={data.status} />} />
            <HeaderRow label="Total Amount" value={formatCurrency(data.total_amount)} />
            <HeaderRow label="Clients" value={data.client_count} />
          </div>
          <TableView
            id="dd-payment-batch-clients-table"
            columns={columns}
            data={rows}
            isLoading={isFetching}
            emptyMessage="No clients in this batch"
            readonly
            striped
          />
        </>
      )}
    </div>
  );
};

export default PaymentBatchDetail;
